// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import logo from '../../screen-copilot-extension/icons/michii_logo_transparent.png';
import { CHROME_STORE_URL } from '../../services/api';
import '../../styles/components/Navbar.css';


const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Shrink effect on scroll
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const onOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [menuOpen]);

  // Close dropdown on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${id}`);
    }
  };
  const current = (path) => (location.pathname === path ? 'page' : undefined);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  const displayInitial = (user?.display_name || user?.email || '').charAt(0).toUpperCase();

  return (
    <header className={`header ${isScrolled ? 'shrunk' : ''}`}>
      <div className="container">
        <Link to="/" className="logo">
          <img src={logo} alt="Michii Logo" />
        </Link>

        {/* Mobile Toggle */}
        <button
          className="mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu"
          aria-expanded={isMobileMenuOpen}
        >
          ☰
        </button>

        <nav className={isMobileMenuOpen ? 'nav-active' : ''}>
          <Link to="/explore" aria-current={current('/explore')} onClick={() => setIsMobileMenuOpen(false)}>Explore</Link>
          <a href="/#how-it-works" onClick={scrollToSection('how-it-works')}>How it works</a>
          <a href="/#pricing" onClick={scrollToSection('pricing')}>Pricing</a>


          {token ? (
            <>
              <Link to="/my-guides" aria-current={current('/my-guides')} onClick={() => setIsMobileMenuOpen(false)}>My Guides</Link>

              {/* ── Avatar button + dropdown ───────────────────────── */}
              <div className="nav-avatar-wrapper" ref={menuRef}>
                <button
                  className="nav-avatar-btn"
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-haspopup="true"
                  aria-expanded={menuOpen}
                  title={user?.email}
                >
                  {displayInitial}
                  {user?.avatar_url && (
                    <img
                      src={user.avatar_url}
                      alt=""
                      onError={(e) => { e.target.style.display = 'none'; }}
                      className="nav-avatar-btn__img"
                    />
                  )}
                  <span className={`nav-avatar-btn__caret${menuOpen ? ' nav-avatar-btn__caret--open' : ''}`} aria-hidden="true">▾</span>
                </button>

                {menuOpen && (
                  <div className="nav-avatar-menu" role="menu">
                    {/* Identity block */}
                    <div className="nav-avatar-menu__identity">
                      <span className="nav-avatar-menu__name">
                        {user?.display_name || user?.email?.split('@')[0] || 'Account'}
                      </span>
                      <span className="nav-avatar-menu__email">{user?.email}</span>
                    </div>

                    <div className="nav-avatar-menu__divider" />

                    <Link
                      to="/profile"
                      className="nav-avatar-menu__item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      Account Settings
                    </Link>

                    <div className="nav-avatar-menu__divider" />

                    <button
                      className="nav-avatar-menu__item"
                      role="menuitem"
                      onClick={toggleTheme}
                    >
                      <i className={theme === 'light' ? "ph ph-moon" : "ph ph-sun"} style={{ fontSize: '15px' }}></i>
                      {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </button>

                    <div className="nav-avatar-menu__divider" />

                    <button
                      className="nav-avatar-menu__item nav-avatar-menu__item--danger"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                        <polyline points="16 17 21 12 16 7"/>
                        <line x1="21" y1="12" x2="9" y2="12"/>
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" aria-current={current('/login')} onClick={() => setIsMobileMenuOpen(false)}>
                Login
              </Link>
              <a
                href={CHROME_STORE_URL}
                className="cta-button primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Add to Chrome
              </a>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;